/**
 * `/p/$slug` — ficha do produto.
 *
 * `hasSession` decide a politica de cache do catalogo (`catalogCachePolicy`): com sessao, a
 * resposta pode carregar preco de clube, e `API §8.1` a marca `private, no-store`. Passar
 * `false` aqui por engano faria o preco personalizado de quem estava logado sobreviver em
 * memoria para o proximo — o cenario do aparelho compartilhado em familia.
 */
import { useApiQueries } from '@farmacia/api-client/query';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { useAnnounce } from '@farmacia/ui';

import { texto } from '../conteudo/index.js';
import { useSessao } from '../app/sessao.jsx';
import { useAdicionarAoCarrinho } from '../carrinho/useAdicionarAoCarrinho.js';
import { useMinhaLoja } from '../loja/contexto.jsx';
import { usePwa } from '../pwa/contexto.jsx';
import { Ficha } from '../telas/Ficha.jsx';

export const Route = createFileRoute('/p/$slug')({ component: TelaDeFicha });

function TelaDeFicha(): ReactNode {
  const { slug } = Route.useParams();
  const queries = useApiQueries();
  const { temSessao } = useSessao();
  const { storeId, hidratando, abrirSeletor } = useMinhaLoja();
  const { conexao } = usePwa();

  /*
   * `storeId ?? ''` sairia como `?storeId=` na URL e voltaria `422`. Sem filial a opcao nao e
   * construida: a ficha mostra o estado de escolha, e nao um erro de validacao disfarcado.
   */
  const consulta = useQuery({
    ...queries.catalog.product(slug, storeId ?? 'sem-filial', temSessao),
    queryFn:
      storeId === null
        ? () => Promise.reject(new Error('Ficha sem filial escolhida: a consulta nao deveria sair.'))
        : queries.catalog.product(slug, storeId, temSessao).queryFn,
    enabled: storeId !== null,
  });

  const { anunciar } = useAnnounce();
  const carrinho = useAdicionarAoCarrinho();

  // `PWA §10.3` — Web Share **com fallback de copiar link**: o iOS antigo e o desktop nao tem
  // `navigator.share`, e um botao que nao faz nada e pior que um botao ausente.
  const podeCompartilhar = typeof navigator !== 'undefined';

  return (
    <Ficha
      produto={consulta.data}
      carregando={consulta.isPending && storeId !== null}
      erro={consulta.error}
      aoTentarDeNovo={() => void consulta.refetch()}
      /*
       * ANTES: `() => void navegar({ to: '/carrinho' })` — a ficha NAVEGAVA sem adicionar, e a
       * pessoa chegava a um carrinho vazio com o produto para tras. O botao dizia "Adicionar" e
       * fazia outra coisa.
       *
       * A ficha adiciona UMA unidade: ela nao tem seletor de quantidade (quem tem e o cartao da
       * grade, com o teto de `availability.maxOrderableQty`). Ajustar quantidade e do
       * `/carrinho`, que ja tem o seletor por linha.
       *
       * // DUVIDA: depois de adicionar, a ficha deveria LEVAR ao carrinho ou ficar onde esta? O
       * // `DESIGN_SYSTEM` nao decide isso em lugar nenhum que eu tenha achado. Fica onde esta,
       * // que e o comportamento do cartao da grade, e a confirmacao vai pela regiao viva
       * // (`§3.5`) — levar a pessoa embora da ficha a cada item some com a opcao de adicionar
       * // um segundo. Reportado.
       */
      aoAdicionar={() => {
        if (consulta.data !== undefined) {
          carrinho.adicionar(
            { id: consulta.data.id, displayName: consulta.data.displayName },
            1,
          );
        }
      }}
      adicionando={carrinho.produtoEmVoo !== null}
      offline={conexao === 'OFFLINE'}
      semLoja={storeId === null && !hidratando}
      aoEscolherLoja={abrirSeletor}
      {...(podeCompartilhar
        ? {
            aoCompartilhar: () => {
              // `PWA §10.3` — Web Share no MVP, com **fallback de copiar link**: o iOS antigo e
              // o desktop nao tem `navigator.share`, e um botao que nao faz nada e pior que um
              // botao ausente.
              //
              // O que sai e a URL; **nunca o preco**: preco colado numa mensagem de WhatsApp e
              // oferta que vincula depois de vencida (CDC art. 30).
              const url = window.location.href;
              if (typeof navigator.share === 'function') {
                void navigator.share({ url });
                return;
              }
              // `navigator.clipboard` esta tipado como sempre presente, mas nao existe em
              // contexto inseguro (http://) nem em WebView antiga — dai o `in` em vez de `?.`.
              if (!('clipboard' in navigator)) return;
              void navigator.clipboard.writeText(url).then(() => {
                anunciar(texto('ficha.linkCopiado'), 'status');
              });
            },
          }
        : {})}
    />
  );
}
